'use client'

import React, { useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import ProtectedRoute from '@/components/ProtectedRoute'
import { 
  TrendingUp, 
  Target, 
  Award, 
  Calendar, 
  MessageCircle, 
  Download,
  BarChart3,
  Settings,
  Clock
} from 'lucide-react'
import {
  PerformanceScoreCard,
  PerformanceLevelBadge,
  CategoryBreakdownCard,
  PerformanceAlertCard
} from '../../components/performance'
import { 
  generateCategoriesData, 
  generatePerformanceAlerts, 
  calculatePerformanceScore,
  getPerformanceLevel,
  getPerformanceInsights,
  calculateTrend
} from '../../lib/performance-utils'
import { MetricCard } from '../../components/MetricCard'
import { usePageTitle } from '@/stores/page-title-store'

function MyEvaluationContent() {
  const { user } = useAuth()
  usePageTitle('Minha Avaliação')
  const [categories, setCategories] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedPeriod, setSelectedPeriod] = useState('current')

  // Load categories data asynchronously
  useEffect(() => {
    const loadCategories = async () => {
      if (!user?.email) {
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        const categoriesData = await generateCategoriesData(user.email)
        setCategories(categoriesData)
      } catch (error) {
        console.error('Error loading categories:', error)
      } finally {
        setLoading(false)
      }
    }

    loadCategories()
  }, [user?.email])

  // Calculate derived data
  const currentScore = calculatePerformanceScore(categories)
  const previousScore = Math.max(50, currentScore - Math.floor(Math.random() * 20) + Math.floor(Math.random() * 10)) // Realistic previous score
  const { config: levelConfig } = getPerformanceLevel(currentScore)
  const trend = calculateTrend(currentScore, previousScore)
  const insights = getPerformanceInsights(categories)

  // Mock alerts for individual
  const alerts = [
    {
      id: 'missing-1on1',
      type: 'missing_review' as const,
      severity: 'medium' as const,
      title: '1:1 Agendado com Seu Gestor',
      description: 'Você tem uma reunião de feedback agendada para próxima semana',
      createdAt: '2 dias',
      resolved: false
    },
    {
      id: 'goal-achievement',
      type: 'goal_missed' as const,
      severity: 'low' as const,
      title: 'Meta de Projeto Próxima',
      description: 'Você está a 85% da sua meta mensal de entregas',
      createdAt: '1 semana',
      resolved: false
    }
  ]

  useEffect(() => {
    // Simulate loading
    const timer = setTimeout(() => setLoading(false), 500)
    return () => clearTimeout(timer)
  }, [])

  if (!user) return null

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-brand-primary border-t-transparent"></div>
      </div>
    )
  }

  return (
    <div className="space-y-8">
        {/* Action Buttons */}
        <div className="flex items-center justify-end space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="form-select bg-brand-dark border-brand-gray text-white rounded-monumenta"
          >
            <option value="current">Período Atual</option>
            <option value="q3-2024">Q3 2024</option>
            <option value="q2-2024">Q2 2024</option>
            <option value="q1-2024">Q1 2024</option>
          </select>

          <button className="btn-ghost">
            <Download size={16} className="mr-2" />
            Exportar
          </button>

          <button className="btn-primary">
            <MessageCircle size={16} className="mr-2" />
            Solicitar Feedback
          </button>
        </div>

        {/* Main Content */}
        <div className="space-y-8">
          
          {/* Performance Overview */}
          <div className="space-y-8">
            
            {/* Performance Score Hero */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <PerformanceScoreCard
                  score={currentScore}
                  previousScore={previousScore}
                  level={levelConfig.label}
                  levelColor={levelConfig.color}
                  daysToNextReview={23}
                  showTrend={true}
                />
              </div>
              
              {/* Quick Stats */}
              <div className="lg:col-span-2 grid grid-cols-2 gap-4">
                <MetricCard
                  title="Categorias Avaliadas"
                  value={categories.length}
                  icon={Target}
                  trend="neutral"
                />
                <MetricCard
                  title="Metas Atingidas"
                  value="7/10"
                  change="+2"
                  icon={Award}
                  trend="up"
                />
                <MetricCard
                  title="Próximo 1:1"
                  value="6 dias"
                  icon={Calendar}
                  trend="neutral"
                />
                <MetricCard
                  title="Feedbacks Recebidos"
                  value="12"
                  change="+3"
                  icon={MessageCircle}
                  trend="up"
                />
              </div>
            </div>

            {/* Category Breakdown */}
            <CategoryBreakdownCard
              categories={categories}
              showWeights={true}
              showEvidence={false}
              compact={false}
            />

            {/* Performance Insights */}
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <h3 className="text-xl font-display font-semibold text-brand-primary mb-6">
                Insights de Performance
              </h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Strengths */}
                <div>
                  <h4 className="font-display font-semibold text-success mb-3 flex items-center">
                    <TrendingUp size={16} className="mr-2" />
                    Pontos Fortes
                  </h4>
                  <ul className="space-y-2">
                    {insights.strengths.map((strength, index) => (
                      <li key={index} className="text-sm text-brand-gray flex items-start">
                        <span className="text-success mr-2">•</span>
                        {strength}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Areas for Improvement */}
                <div>
                  <h4 className="font-display font-semibold text-warning mb-3 flex items-center">
                    <Target size={16} className="mr-2" />
                    Oportunidades
                  </h4>
                  <ul className="space-y-2">
                    {insights.improvements.map((improvement, index) => (
                      <li key={index} className="text-sm text-brand-gray flex items-start">
                        <span className="text-warning mr-2">•</span>
                        {improvement}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Recommended Actions */}
                <div>
                  <h4 className="font-display font-semibold text-brand-blue mb-3 flex items-center">
                    <Settings size={16} className="mr-2" />
                    Ações Recomendadas
                  </h4>
                  <ul className="space-y-2">
                    {insights.actions.map((action, index) => (
                      <li key={index} className="text-sm text-brand-gray flex items-start">
                        <span className="text-brand-blue mr-2">•</span>
                        {action}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* Historical Performance Chart */}
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-display font-semibold text-brand-primary">
                  Evolução da Performance
                </h3>
                <BarChart3 className="text-brand-gray" size={24} />
              </div>
              
              {/* Mock Chart */}
              <div className="h-64 bg-brand-dark/30 rounded-monumenta flex items-center justify-center">
                <div className="text-center">
                  <BarChart3 className="text-brand-gray mx-auto mb-4" size={48} />
                  <p className="text-brand-gray">Gráfico de evolução histórica</p>
                  <p className="text-sm text-brand-gray/70 mt-2">
                    Dados dos últimos 6 meses • Score atual: {currentScore}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Actions & Alerts */}
          {/* 
          <div className="xl:col-span-4 space-y-6">
            
            {/* Performance Level Badge */}
            {/*
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10 text-center">
              <h3 className="font-display font-semibold text-white mb-4">
                Nível Atual
              </h3>
              <PerformanceLevelBadge 
                level={levelConfig.label} 
                score={currentScore} 
                size="lg" 
              />
              <p className="text-sm text-brand-gray mt-4">
                Baseado em {categories.length} categorias avaliadas
              </p>
            </div>

            {/* Alerts & Notifications */}
            {/*
            <PerformanceAlertCard
              alerts={alerts}
              showResolved={false}
            />

            {/* Quick Actions */}
            {/*
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <h3 className="font-display font-semibold text-brand-primary mb-4">
                Ações Rápidas
              </h3>
              <div className="space-y-3">
                <button className="w-full text-left flex items-start space-x-3 p-3 rounded-monumenta hover:bg-brand-gray/10 transition-colors">
                  <Calendar className="text-brand-blue flex-shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <div className="font-display font-medium text-white">Agendar 1:1</div>
                    <div className="text-xs text-brand-gray">Com seu gestor direto</div>
                  </div>
                </button>
                
                <button className="w-full text-left flex items-start space-x-3 p-3 rounded-monumenta hover:bg-brand-gray/10 transition-colors">
                  <Target className="text-warning flex-shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <div className="font-display font-medium text-white">Definir Metas</div>
                    <div className="text-xs text-brand-gray">Para o próximo período</div>
                  </div>
                </button>
                
                <button className="w-full text-left flex items-start space-x-3 p-3 rounded-monumenta hover:bg-brand-gray/10 transition-colors">
                  <Award className="text-success flex-shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <div className="font-display font-medium text-white">Ver Conquistas</div>
                    <div className="text-xs text-brand-gray">Histórico de reconhecimentos</div>
                  </div>
                </button>
                
                <button className="w-full text-left flex items-start space-x-3 p-3 rounded-monumenta hover:bg-brand-gray/10 transition-colors">
                  <Clock className="text-brand-primary flex-shrink-0 mt-0.5" size={20} />
                  <div className="flex-1">
                    <div className="font-display font-medium text-white">PDI</div>
                    <div className="text-xs text-brand-gray">Plano de Desenvolvimento Individual</div>
                  </div>
                </button>
              </div>
            </div>

            {/* Recent Activity */}
            {/*
            <div className="bg-brand-dark-light rounded-monumenta p-6 border border-brand-gray/10">
              <h3 className="font-display font-semibold text-brand-primary mb-4">
                Atividade Recente
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-success rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">Meta de projeto atingida</p>
                    <p className="text-xs text-brand-gray">Há 2 dias</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-brand-blue rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">Feedback recebido</p>
                    <p className="text-xs text-brand-gray">Há 5 dias</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-warning rounded-full mt-2"></div>
                  <div>
                    <p className="text-sm text-white font-medium">1:1 agendado</p>
                    <p className="text-xs text-brand-gray">Há 1 semana</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          */}
        </div>
      </div>
  )
}

export default function MyEvaluationPage() {
  return (
    <ProtectedRoute>
      <MyEvaluationContent />
    </ProtectedRoute>
  )
}
